import type { CompiledWorldChunk } from '../../chunk-schema/src/index.ts'

export type ChunkOffset = { readonly x: number; readonly z: number }

export type NearestRoadSample = {
  readonly distance: number
  readonly height: number
  readonly width: number
  readonly tangentX: number
  readonly tangentZ: number
  readonly roadId: string
  readonly flags: number
}

export type RoadSampleQueryOptions = {
  /** Select metadata from the vertical road owner nearest this physical surface. */
  readonly referenceHeight?: number
  /** Reject centreline metadata outside its rendered lateral ownership envelope. */
  readonly maximumLateralMargin?: number
  /** Reject metadata belonging to another grade-separated surface. */
  readonly maximumVerticalDelta?: number
}

/**
 * The one continuity rule used by both vehicle support and its F2 authority
 * visualisation. Samples farther apart than this are separate clipped spans,
 * even when they belong to the same source road.
 */
export const MAX_ROAD_SAMPLE_SEGMENT_METERS = 24

export function isContinuousRoadSampleSegment(
  roads: CompiledWorldChunk['roads'],
  index: number,
): boolean {
  if (index <= 0 || index >= roads.widths.length) return false
  if (roads.roadIndex[index - 1] !== roads.roadIndex[index]) return false
  const current = index * 3
  const previous = current - 3
  const dx = roads.positions[current] - roads.positions[previous]
  const dz = roads.positions[current + 2] - roads.positions[previous + 2]
  const lengthSquared = dx * dx + dz * dz
  return lengthSquared >= 1e-6 && lengthSquared <= MAX_ROAD_SAMPLE_SEGMENT_METERS ** 2
}

export function chunkContainsWorldPoint(
  chunk: CompiledWorldChunk,
  offset: ChunkOffset,
  x: number,
  z: number,
  padding = 0,
) {
  const localX = x - offset.x
  const localZ = z - offset.z
  const bounds = chunk.localBounds
  return localX >= bounds.minX - padding && localX <= bounds.maxX + padding && localZ >= bounds.minZ - padding && localZ <= bounds.maxZ + padding
}

export function chunkDistanceToWorldPoint(
  chunk: CompiledWorldChunk,
  offset: ChunkOffset,
  x: number,
  z: number,
) {
  const localX = x - offset.x
  const localZ = z - offset.z
  const bounds = chunk.localBounds
  const dx = Math.max(bounds.minX - localX, 0, localX - bounds.maxX)
  const dz = Math.max(bounds.minZ - localZ, 0, localZ - bounds.maxZ)
  return Math.hypot(dx, dz)
}

export function sampleChunkTerrain(
  chunk: CompiledWorldChunk,
  offset: ChunkOffset,
  x: number,
  z: number,
) {
  const grid = chunk.terrain
  const column = (x - offset.x - grid.origin.x) / grid.stepX
  const row = (z - offset.z - grid.origin.z) / grid.stepZ
  if (column < -0.01 || row < -0.01 || column > grid.columns - 0.99 || row > grid.rows - 0.99) return undefined
  const x0 = Math.max(0, Math.min(grid.columns - 1, Math.floor(column)))
  const z0 = Math.max(0, Math.min(grid.rows - 1, Math.floor(row)))
  const x1 = Math.min(grid.columns - 1, x0 + 1)
  const z1 = Math.min(grid.rows - 1, z0 + 1)
  const tx = Math.max(0, Math.min(1, column - x0))
  const tz = Math.max(0, Math.min(1, row - z0))
  const north = grid.heights[z0 * grid.columns + x0] * (1 - tx) + grid.heights[z0 * grid.columns + x1] * tx
  const south = grid.heights[z1 * grid.columns + x0] * (1 - tx) + grid.heights[z1 * grid.columns + x1] * tx
  return north * (1 - tz) + south * tz
}

/**
 * Reports whether every source sample contributing to the interpolated terrain
 * height came from the elevation provider. An absent mask is deliberately not
 * accepted here: QA uses this to catch stale/fallback payloads that can still
 * contain finite-looking zeroes.
 */
export function sampleChunkTerrainValidity(
  chunk: CompiledWorldChunk,
  offset: ChunkOffset,
  x: number,
  z: number,
) {
  const grid = chunk.terrain
  const validity = grid.validity
  if (!validity) return false
  const column = (x - offset.x - grid.origin.x) / grid.stepX
  const row = (z - offset.z - grid.origin.z) / grid.stepZ
  if (column < -0.01 || row < -0.01 || column > grid.columns - 0.99 || row > grid.rows - 0.99) return false
  const x0 = Math.max(0, Math.min(grid.columns - 1, Math.floor(column)))
  const z0 = Math.max(0, Math.min(grid.rows - 1, Math.floor(row)))
  const x1 = Math.min(grid.columns - 1, x0 + 1)
  const z1 = Math.min(grid.rows - 1, z0 + 1)
  return validity[z0 * grid.columns + x0] !== 0
    && validity[z0 * grid.columns + x1] !== 0
    && validity[z1 * grid.columns + x0] !== 0
    && validity[z1 * grid.columns + x1] !== 0
}

export function nearestChunkRoad(
  chunk: CompiledWorldChunk,
  offset: ChunkOffset,
  x: number,
  z: number,
  options: RoadSampleQueryOptions = {},
): NearestRoadSample | undefined {
  const roads = chunk.roads
  const localX = x - offset.x
  const localZ = z - offset.z
  let best: NearestRoadSample | undefined
  let bestScore = Number.POSITIVE_INFINITY
  const consider = (
    distance: number,
    height: number,
    width: number,
    tangentX: number,
    tangentZ: number,
    roadIndex: number,
    flags: number,
  ) => {
    if (options.maximumLateralMargin !== undefined
      && distance > width * 0.5 + options.maximumLateralMargin) return
    const verticalDelta = options.referenceHeight === undefined
      ? 0
      : Math.abs(height - options.referenceHeight)
    if (options.maximumVerticalDelta !== undefined && verticalDelta > options.maximumVerticalDelta) return
    const score = options.referenceHeight === undefined
      ? distance
      : distance / Math.max(1, width * 0.5) + verticalDelta * 0.72
    if (best && score >= bestScore) return
    bestScore = score
    best = {
      distance,
      height,
      width,
      tangentX,
      tangentZ,
      roadId: roads.roadIds[roadIndex] || 'UNNAMED ROAD',
      flags,
    }
  }
  for (let index = 0; index < roads.widths.length; index += 1) {
    const px = roads.positions[index * 3]
    const py = roads.positions[index * 3 + 1]
    const pz = roads.positions[index * 3 + 2]
    consider(
      Math.hypot(localX - px, localZ - pz),
      py,
      roads.widths[index],
      roads.tangents[index * 2],
      roads.tangents[index * 2 + 1],
      roads.roadIndex[index],
      roads.flags[index],
    )
    if (!isContinuousRoadSampleSegment(roads, index)) continue
    const ax = roads.positions[(index - 1) * 3]
    const ay = roads.positions[(index - 1) * 3 + 1]
    const az = roads.positions[(index - 1) * 3 + 2]
    const dx = px - ax
    const dz = pz - az
    const lengthSquared = dx * dx + dz * dz
    const t = Math.max(0, Math.min(1, ((localX - ax) * dx + (localZ - az) * dz) / lengthSquared))
    const tangentX = roads.tangents[(index - 1) * 2] * (1 - t) + roads.tangents[index * 2] * t
    const tangentZ = roads.tangents[(index - 1) * 2 + 1] * (1 - t) + roads.tangents[index * 2 + 1] * t
    const tangentLength = Math.hypot(tangentX, tangentZ) || 1
    consider(
      Math.hypot(localX - (ax + dx * t), localZ - (az + dz * t)),
      ay * (1 - t) + py * t,
      roads.widths[index - 1] * (1 - t) + roads.widths[index] * t,
      tangentX / tangentLength,
      tangentZ / tangentLength,
      roads.roadIndex[index],
      roads.flags[index - 1] | roads.flags[index],
    )
  }
  return best
}
